import React from "react";
import { serverSideTranslations } from "@/libs/i18n/serverSideTranslations";
import { GetServerSidePropsContext } from "next";
import { getDehydratedState, mergeDehydratedStates } from "@/utils/ReactQuery";
import { ProductQuery } from "@/services/product/ProductQuery";
import { BannerQuery } from "@/services/banner/BannerQuery";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import RecommendTactSuit from "@/components/home/RecommendTactSuit";
import HomeHero from "@/components/home/HomeHero";
import ChooseTactSuit from "@/components/home/ChooseTactSuit";
import StartWithConfidence from "@/components/home/StartWithConfidence";
import PlayEveryWay from "@/components/home/PlayEveryWay";
import PlayerReactions from "@/components/home/PlayerReactions";
import ProductCategories from "@/components/home/ProductCategories";
import Subscribe from "@/components/home/Subscribe";
import PageTemplate from "@/components/templates/PageTemplate";
import { Button } from "@/v2-components/ui/button";
import HomeFloatingPopups from "@/v2-components/home-floating-popups";
import { isGta5GiveawayOpen } from "@/constants/gta5-giveaway";
import { getShippableGeo } from "@/libs/getGeo";
import { getServerSideCountryCode } from "@/utils/LocaleUtil";
import { getCookie } from "cookies-next";
import { COUNTRY_CODE_KEY } from "@/constants/CookieValues";
import { parseCountryCode } from "@/libs/locale/cookie";

/**
 * ⚠️ 국가 값이 **두 개**다. 축이 달라서 하나로 합치면 안 된다.
 *
 * - `countryCode` = `getShippableGeo(req)` → **IP** 기준. `PageTemplate` 이 이걸로
 *   `SuggestLocale`(지역 제안 배너)을 띄운다. 배너의 존재 이유가 "IP 는 여기인데 지금
 *   보는 locale 과 다르다" 이므로 쿠키 기준으로 바꾸면 **배너가 영영 안 뜬다.**
 * - `commerceCountryCode` = **`bh_ccode` 쿠키 우선, 없으면 IP**. 사용자가 국가 선택기로
 *   고른 값이 이긴다. 구매 혜택 카드처럼 "이 사용자에게 무엇을 약속하는가" 에 걸린 표시는
 *   이쪽을 따른다.
 *
 * ⚠️ 여기서 `getServerSideCountryCode` 를 쓰면 안 된다. 그 함수는 고른 국가가 현재 locale 의
 * 언어를 지원하지 않으면 **locale 의 기본 국가로 덮어쓴다**(`getResolvedCountryCode`).
 * 가격/로케일 판정에는 맞지만 여기서는 `bh_ccode=AL` + `/ko` 가 `KR` 로 뒤집혀
 * **알바니아 고객에게 "한국 전 지역 무료 배송" 을 약속**하게 된다(실측 확인).
 *
 * 자세한 건 docs/locale-middleware-policy.md (bh_ccode vs bhap_country).
 */
export default function HomePage({
  countryCode,
  commerceCountryCode,
  gta5Open,
}: {
  countryCode: string;
  commerceCountryCode: string;
  gta5Open: boolean;
}) {
  return (
    <PageTemplate countryCode={countryCode}>
      {/**
       * ★**떠 있는 팝업은 하나다** — 캠페인을 슬라이드로 모은다 (2026-09-11).
       *
       * 종전엔 `<WelcomeCouponPopup />` 과 `<Gta5GiveawayPopup />` 을 **나란히**
       * 마운트했고, 바로 이 자리 주석이 그 결함을 **이미 알고 있었다**:
       *
       * > ⚠️ 웰컴 쿠폰 팝업과 **동시에 뜰 수 있다.** … 캠페인이 실제로 겹치면
       * > 우선순위를 정해야 한다(둘 다 띄우지 않는 쪽이 맞다).
       *
       * 번호 없는 유예라 유실됐다 — 실측하니 **데스크탑에서 완전히 같은 자리·같은
       * `z-50`** 이고 상호 배타 조건이 0건이었다. 겹치는 모집단은 **비로그인 신규
       * 방문자 ∩ 캠페인 기간**이다.
       *
       * 판정은 각자의 것 그대로다(쿠폰 `useWelcomeCouponOffer` · GTA5 서버
       * `gta5Open`). 조합기는 **모아서 순서만** 정한다.
       */}
      <HomeFloatingPopups gta5Open={gta5Open} />
      <PageTemplate.Content className="min-h-screen w-full bg-white">
        {/* 히어로는 풀블리드라 Gap 을 두지 않는다 */}
        <HomeHero />
        {/* ── 이하 홈 리뉴얼 시안 순서 (기획 확정 2026-08-14) ──
            시안: 히어로 → 플랫폼 → 플레이어 반응 → 제품 카드 → 구매 혜택 → 제품 카테고리 → 뉴스레터

            홈에서 내린 섹션과 대체 관계:
            - HomeTopItems → HomeHero (배너 캐러셀 → 시안의 풀블리드 히어로)
            - KeyProducts → ChooseTactSuit (Air/Pro/TactSleeve 3장 → 시안의 Air/Pro 2장)
            - ImmersionBeyondComparison → PlayEveryWay (같은 "플랫폼별 몰입" 자리).
              이 컴포넌트는 TactSuit 페이지 등 다른 곳에서 계속 쓰인다.
            - GameClips → PlayerReactions (같은 `useGameClips` 데이터, 표현만 스트립으로)
            - LovedByPlayers → PlayerReactions 로 흡수 (둘 다 플레이어 반응)
            - WhyTactSuit / InsideBhaptics → 시안에 대응 섹션 없음
            - BundlesCover → 시안에 없어 홈에서만 내렸다. **TactSuit 페이지에서는 계속 쓰인다.**

            위에서 대체된 6개 컴포넌트(HomeTopItems·KeyProducts·GameClips·LovedByPlayers·
            WhyTactSuit·InsideBhaptics)는 참조가 0 이 되어 **삭제했다**. 되살릴 일이 생기면
            git 이력에서 꺼내면 된다.
            ⚠️ ImmersionBeyondComparison·BundlesCover 는 **지우지 않았다** — TactSuit 페이지 등
            다른 화면에서 계속 쓴다. */}
        {/* 모바일 시안은 히어로 → 첫 섹션만 48px 이다(Body 상단 패딩). 섹션 사이는 64. */}
        <Gap className="mt-12 laptop:mt-24">
          <PlayEveryWay />
        </Gap>
        <Gap>
          <PlayerReactions />
        </Gap>
        <Gap>
          <ChooseTactSuit />
        </Gap>
        <Gap>
          {/* 무료 배송 카드 노출은 사용자가 고른 커머스 국가를 따른다 — IP 가 아니다.
              국가 선택기로 알바니아를 고르면 즉시 해외 구성으로 바뀌어야 한다. */}
          <StartWithConfidence countryCode={commerceCountryCode} />
        </Gap>
        <Gap>
          <ProductCategories />
        </Gap>
        {/* 시안의 "제품 추천받기" CTA.
            ⚠️ 기존 인트로 배너("당신에게 필요한 TactSuit은?" + 제품 이미지 나열)는 시안에 없다 —
            배너 없이 버튼 하나만 둔다. 팝업 자체는 기존 RecommendTactSuit 을 그대로 쓴다.

            ⚠️ **모바일에도 노출한다** — 시안의 모바일 보드에는 이 CTA 가 없었고(디자이너 공유
            HTML `.s7-cta-wrap{display:none}`) 그래서 처음에는 `hidden laptop:flex` 로 감췄는데,
            **전환 동선이 모바일에서만 하나 사라지는 게 문제라 노출로 되돌렸다**(요청).
            시안과 다른 방향이므로, 다시 감추려면 이 한 줄만 `hidden laptop:flex` 로 바꾸면 된다. */}
        <div className="mt-12 flex justify-center px-4">
          <RecommendTactSuit
            renderTrigger={(open) => <RecommendCta onClick={open} />}
          />
        </div>
        {/* ⚠️ 뉴스레터는 **데스크톱에만** 있다. 모바일 보드(영문 29065:21876 / 국문 29083:22019)는
            제품 카테고리에서 Body 가 끝나고 곧장 푸터로 간다 — 모바일에서는 **푸터 안의 뉴스레터**가
            이 역할을 대신한다(같은 화면에 가입 폼이 두 번 나오지 않게).

            바깥 여백은 감추지 않는다. 모바일 보드도 제품 카테고리 → 푸터 사이에 64px 을 두고,
            데스크톱은 위 96 + 아래 96 이다. */}
        <div className="mt-16 laptop:mt-24 laptop:pb-24">
          <div className="hidden laptop:block">
            <Subscribe />
          </div>
        </div>
      </PageTemplate.Content>
    </PageTemplate>
  );
}

/** 피그마 28983:88661 — border blue-60 / px 32 / py 14 / r 24 / 16px Bold. */
const RecommendCta = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTrans(["index"]);
  return (
    <Button
      type="button"
      onClick={onClick}
      variant="blue-outline"
      shape="pill"
      size="none"
      className="px-8 py-3.5 text-lg font-700"
    >
      {t("Find Your Fit")}
    </Button>
  );
};

const Gap = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => (
  <div className={cn("mt-16 laptop:mt-24", className)}>{children}</div>
);

export async function getServerSideProps({
  req,
  res,
  /**
   * ⚠️ 기본값을 둔다 — 실제 Next 는 항상 `{}` 이상을 주지만, 형제 `locale = ""` 와
   * 같은 이유로 호출부가 생략해도 터지지 않게 한다(테스트 하네스가 그렇다).
   */
  query = {},
  locale = "",
}: GetServerSidePropsContext) {
  // default locale URL 은 country prefix 없이 공유되므로 country 별 응답을
  // CDN 캐시하면 cross-country 오염이 발생한다. (docs/locale-middleware-policy.md §1.4)
  const resolvedCountryCode = getServerSideCountryCode(
    locale ?? "en",
    req,
    res,
  );

  const [productsState, bannersState, countryCode, translations] =
    await Promise.all([
      getDehydratedState(
        ProductQuery.products(resolvedCountryCode === "US" ? "US" : "KR"),
      ),
      getDehydratedState(BannerQuery.banners()),
      getShippableGeo(req),
      serverSideTranslations(locale, [
        "index",
        "gnb",
        "tactsuit",
        // ★Audio-to-Haptics 팝업이 `CHAIN`(VR ATH 밴드) 문구를 재사용한다 —
        // 그 키가 `games` 에 있다(#5516). 없으면 한국어에서 영문이 그대로 나간다.
        "games",
        // ★GTA5 팝업 배너 문구가 `event` 에 있다(#5739 다국어). 없으면 키 이름이
        // 그대로 화면에 나간다 — 위 `games` 와 같은 클래스다.
        "event",
      ]),
    ]);

  // title · description 은 넘기지 않는다 — `Header` 가 언어별 `Home`(`static-seo-data.ts`)으로 채운다.
  // 여기서 영어로 넘기면 7개 언어가 전부 영어가 된다(2026-02-26 ~ 회귀).
  const seo = {
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "bHaptics",
        url: "https://www.bhaptics.com",
        logo: "https://www.bhaptics.com/images/logo.png",
        sameAs: [
          "https://twitter.com/bhaptics",
          "https://www.facebook.com/bhaptics",
          "https://www.youtube.com/@bhaptics",
          "https://www.linkedin.com/company/bhaptics",
        ],
        contactPoint: {
          "@type": "ContactPoint",
          email: "support@bhaptics.com",
          contactType: "customer support",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "bHaptics",
        url: "https://www.bhaptics.com",
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate:
              "https://www.bhaptics.com/games?search={search_term_string}",
          },
          "query-input": "required name=search_term_string",
        },
      },
    ],
  };

  // 국가 선택기로 고른 값이 최우선. 없으면 IP(배송 가능 국가만, 아니면 "").
  // "" 는 판별 실패로 취급돼 해외 구성으로 떨어진다 — 무료 배송을 잘못 약속하지 않는 쪽.
  const commerceCountryCode =
    parseCountryCode(getCookie(COUNTRY_CODE_KEY, { req, res })) ?? countryCode;

  return {
    props: {
      countryCode,
      commerceCountryCode,
      /**
       * GTA5 팝업 노출 여부 — **서버에서** 판정해 내려 준다.
       *
       * 클라이언트에서 시각을 재면 사용자 시계에 좌우되고, 무엇보다 랜딩 페이지의
       * 판정(`getServerSideProps`)과 **갈릴 수 있다** — 그러면 팝업은 떴는데 눌러 보니
       * 404 다. 같은 함수를 같은 층에서 부른다.
       */
      gta5Open: isGta5GiveawayOpen({ now: new Date() }),
      seo,
      dehydratedState: mergeDehydratedStates(productsState, bannersState),
      ...translations,
    },
  };
}
